import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { StyleSheet, View } from 'react-native';
import { requestEmailVerification, requestWorkEmailVerification, resetPasscode, signInV2, } from 'actions';
import { ButtonGroup, RequestActionView } from 'design-system';
import Button from 'design-system/Button';
import { IconEmail } from 'design-system/icons';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { useInterval } from 'hooks';
import useFetchOld, { muteErrorAndResult } from 'hooks/useFetch';
import useOpenEmailApp from 'hooks/useOpenEmailApp';
import { useAppSelector } from 'store/hooks';
import links from 'utils/links';
import { isPrivateEmail } from 'utils/validation';
import useSignIn, { ALT_STEP_LABELS } from 'login/hooks/useSignIn';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
export const EmailVerificationScreen = ({ route, navigation, }) => {
    const verifyingEmail = useAppSelector((store) => store.user.verifyingEmail);
    const verifyingEmployer = useAppSelector((store) => store.user.verifyingEmployer);
    const signInState = useAppSelector((store) => store.signIn);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const [currentlyVerifyingEmail] = useState(verifyingEmail);
    const [currentlyVerifyingEmployer] = useState(verifyingEmployer);
    const [currentNextStep] = useState(signInState.currentState?.nextStep);
    const [, , alternativeStep, , nextAlternativeStep] = useSignIn();
    const { flow } = route.params;
    const [onPressOpenEmail, hasEmailApps] = useOpenEmailApp();
    const [timeLeft, setTimeLeft] = useState(route.params?.startAtSeconds || 60);
    useInterval(() => {
        if (timeLeft && timeLeft > 0) {
            setTimeLeft(timeLeft - 1);
        }
    }, timeLeft ? 1000 : undefined);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={flow === 'verifyEmployer'
                    ? HELP_ARTICLES_KEY.ORGANISATION
                    : undefined}/>),
            headerTitle: flow === 'resetPin' || flow === 'updatePhone'
                ? ''
                : 'Verify your email',
        });
    }, [flow]);
    useEffect(() => {
        if (flow === 'updatePhone')
            return;
        // Verification complete
        if (verifyingEmail !== currentlyVerifyingEmail ||
            currentlyVerifyingEmployer?.email !== verifyingEmployer?.email) {
            if (flow === 'resetPin') {
                if (route.params.popOnComplete) {
                    navigation.pop();
                }
                navigation.navigate('SetupPasscode', {
                    keyBack: null,
                    popOnComplete: route.params.popOnComplete,
                });
            }
            else if (flow === 'verifyEmployer') {
                navigation.navigate('Success', {
                    subTitle: 'Your Emma Ultimate license\nis now active!',
                    onContinue: () => {
                        navigation.popToTop();
                        navigation.navigate('Feed');
                        navigation.navigate('PremiumSubscriptionWelcome', {});
                        return true;
                    },
                });
            }
            else {
                navigation.navigate('PersonalDetails');
            }
        }
    }, [
        verifyingEmail,
        currentlyVerifyingEmail,
        navigation,
        route.params,
        flow,
        currentlyVerifyingEmployer?.email,
        verifyingEmployer?.email,
    ]);
    const onPressSendEmailAgain = async () => {
        const emailRequest = () => {
            if (flow === 'resetPin') {
                if (currentNextStep === 'pin_reset_email_otp') {
                    return signInV2({
                        ...signInState.signInParameters,
                        sendPinResetEmail: true,
                    });
                }
                return resetPasscode(true);
            }
            if (flow === 'updatePhone' && signInState.signInParameters?.updatePhone) {
                return signInV2({
                    ...signInState.signInParameters,
                    sendEmailOtp: true,
                });
            }
            if (flow === 'verifyEmployer') {
                return requestWorkEmailVerification(route.params.newEmail);
            }
            return requestEmailVerification(verifyingEmail ?? '');
        };
        const result = await fetch(emailRequest());
        if (result) {
            setTimeLeft(60);
        }
    };
    const subTitle = useMemo(() => {
        let email;
        if (flow === 'change' || flow === 'verifyEmployer') {
            email = route.params.newEmail;
        }
        else if (flow === 'resetPin' || flow === 'updatePhone') {
            email = route.params.recipientEmail;
        }
        return (<Text TextThin-16 Secondary centered>
        We’ve sent
        {email ? ` an email to \n${email}` : ' you an email'}. Click on the link
        to verify.
        {email && isPrivateEmail(email) ? (<>
            <Text TextThin-16 Secondary centered>
              {' '}
              Keep in mind, this is Apple’s Private Relay email which hides your
              real one with a private address.{' '}
            </Text>
            <Text Text-16 Primary onPress={() => links.openKnownLink('privateRelayEmailHelp')} suppressHighlighting>
              Learn more
            </Text>
          </>) : null}
      </Text>);
    }, [flow, route.params]);
    const alternativeStepToShow = useMemo(() => currentNextStep === 'pin_reset_email_otp' &&
        signInState.currentState?.alternativeSteps
        ? nextAlternativeStep(['pin_reset_email'])
        : undefined, [
        currentNextStep,
        nextAlternativeStep,
        signInState.currentState?.alternativeSteps,
    ]);
    const onPressAlternativeStep = useCallback(() => {
        alternativeStep(undefined, ['pin_reset_email']);
    }, [alternativeStep]);
    return (<RequestActionView icon={<IconEmail width={54} height={54} colorKey="brand"/>} title="Check your inbox" subTitle={subTitle} iconScale={3.68} onDark>
      <ButtonGroup type="floating">
        {timeLeft ? (<View>
            <Text TextThin-16 Secondary style={styles.details}>
              You can ask for a new link in {timeLeft} seconds
            </Text>
          </View>) : (<View>
            <Text TextThin-16 Secondary style={styles.details}>
              You can now ask for a new link
            </Text>
            <Button {...(alternativeStepToShow
            ? { brandBorder: true }
            : { brandReversed: true })} isFetching={isFetching} onPress={onPressSendEmailAgain} title="Send the email again"/>
          </View>)}
        {alternativeStepToShow ? (<Button brandReversed isFetching={isFetching} onPress={onPressAlternativeStep} title={ALT_STEP_LABELS[alternativeStepToShow]}/>) : null}
        {hasEmailApps ? (<Button title="Open email" style={styles.button} onPress={onPressOpenEmail} brand/>) : null}
      </ButtonGroup>
    </RequestActionView>);
};
const styles = StyleSheet.create({
    details: {
        textAlign: 'center',
        marginBottom: rem(16),
    },
    button: {
        marginBottom: rem(10),
    },
});
